import React, { useEffect, useRef, useState } from "react";
import { Mic, MessageSquareReply, GitCompareArrows, PlayCircle } from "lucide-react";
import { api } from "@/lib/api";

const BASE = process.env.REACT_APP_BACKEND_URL;
const fmt = (d) => new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

const Compare = ({ first, latest }) => {
  const a = useRef(null), b = useRef(null);
  const playBoth = () => {
    b.current.pause(); b.current.currentTime = 0;
    a.current.currentTime = 0;
    a.current.onended = () => { a.current.onended = null; b.current.play(); };
    a.current.play();
  };
  return (
    <div className="mt-5 rounded-2xl border border-primary/40 bg-primary/5 p-5" data-testid="takes-compare">
      <p className="flex items-center gap-2 text-sm font-semibold text-foreground"><GitCompareArrows size={16} className="text-primary" /> Compare first vs latest</p>
      <div className="mt-3 grid gap-4 sm:grid-cols-2">
        {[["First take", first, a, "first"], ["Latest take", latest, b, "latest"]].map(([label, t, ref, k]) => (
          <div key={k} className="rounded-xl bg-background p-3">
            <p className="text-xs text-muted-foreground"><strong className="text-foreground">{label}</strong> · {fmt(t.created_at)}</p>
            <p className="mt-0.5 truncate text-xs text-muted-foreground">{t.message.replace(/^Play-along take — /, "").split("\n\n")[0]}</p>
            <audio ref={ref} controls src={`${BASE}${t.attachment_url}`} className="mt-2 w-full" data-testid={`compare-audio-${k}`} />
          </div>
        ))}
      </div>
      <button onClick={playBoth} data-testid="compare-play-both" className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground transition-transform hover:scale-105">
        <PlayCircle size={16} /> Play both one after the other
      </button>
    </div>
  );
};

export const TakesTimeline = () => {
  const [takes, setTakes] = useState(null);
  useEffect(() => { api.get("/tutor-questions/takes").then(({ data }) => setTakes(data.takes || [])).catch(() => setTakes([])); }, []);
  if (!takes) return null;

  return (
    <section className="mt-12 rounded-2xl border border-border bg-card p-6" data-testid="takes-timeline">
      <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-primary"><Mic size={14} /> Hear your progress</p>
      <h2 className="mt-1 font-serif text-2xl">My recorded takes</h2>
      {takes.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground" data-testid="takes-empty">No takes yet. Record yourself with a play-along track in any lesson from lesson 4 onwards and send it to Jason.</p>
      ) : (
        <>
        {takes.filter((t) => t.attachment_url).length >= 2 && (() => { const w = takes.filter((t) => t.attachment_url); return <Compare first={w[w.length - 1]} latest={w[0]} />; })()}
        <ol className="relative mt-6 space-y-6 border-l border-primary/30 pl-6">
          {takes.map((t) => {
            const [first, ...rest] = t.message.replace(/^Play-along take — /, "").split("\n\n");
            return (
              <li key={t.id} className="relative" data-testid={`take-${t.id}`}>
                <span className="absolute -left-[31px] top-1 h-3 w-3 rounded-full bg-primary" />
                <p className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })} · {t.course}</p>
                <p className="mt-1 text-sm font-medium text-foreground">{first}</p>
                {rest.length > 0 && <p className="mt-1 text-xs italic text-muted-foreground">"{rest.join(" ")}"</p>}
                {t.attachment_url && <audio controls src={`${BASE}${t.attachment_url}`} className="mt-2 w-full max-w-md" data-testid={`take-audio-${t.id}`} />}
                {t.reply ? (
                  <div className="mt-3 rounded-xl border-l-2 border-primary bg-primary/5 p-3 text-sm" data-testid={`take-reply-${t.id}`}>
                    <p className="flex items-center gap-1.5 text-xs font-semibold text-primary"><MessageSquareReply size={13} /> Jason's feedback</p>
                    {t.reply.message && <p className="mt-1 whitespace-pre-line">{t.reply.message}</p>}
                    {t.reply.attachment_kind === "audio" && <audio controls src={`${BASE}${t.reply.attachment_url}`} className="mt-2 w-full max-w-md" />}
                  </div>
                ) : <p className="mt-2 text-xs text-muted-foreground">Waiting for Jason's feedback (within 24 hours)</p>}
              </li>
            );
          })}
        </ol>
        </>
      )}
    </section>
  );
};
